import React from 'react';
import { Activity, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров TDEE
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  activityLevels,
  goals,
  ageMin,
  ageMax,
  weightMin,
  weightMax,
  heightMin,
  heightMax,
}) => {
  // Обработчик изменения возраста
  const handleAgeChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      age: newValue
    }));
  };
  
  // Обработчик изменения веса
  const handleWeightChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      weight: newValue
    }));
  };
  
  // Обработчик изменения роста
  const handleHeightChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      height: newValue
    }));
  };
  
  // Парсинг числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Предопределенные значения для быстрого выбора
  const maleSuggestions = [
    { age: '25', weight: '70', height: '175' },
    { age: '35', weight: '80', height: '180' },
    { age: '45', weight: '85', height: '177' },
    { age: '55', weight: '82', height: '175' }
  ];
  
  const femaleSuggestions = [
    { age: '25', weight: '55', height: '165' },
    { age: '35', weight: '60', height: '167' },
    { age: '45', weight: '65', height: '165' },
    { age: '55', weight: '68', height: '163' }
  ];
  
  // Выбираем подходящие варианты в зависимости от пола
  const suggestions = formData.gender === 'male' ? maleSuggestions : femaleSuggestions;
  
  // Функция расчета
  const calculateTDEE = () => {
    // Инициируем расчет через клик по кнопке в основной форме
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-emerald-500" />
        Быстрая настройка
      </h3>
      
      <div className="space-y-5">
        {/* Пол */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Пол</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, gender: 'male' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.gender === 'male' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Мужской
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, gender: 'female' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.gender === 'female' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Женский
            </button>
          </div>
        </div>
        
        {/* Возраст */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="ageSlider" className="text-gray-600 dark:text-gray-400">Возраст</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.age} лет</span>
          </div>
          <input
            type="range"
            id="ageSlider"
            min={ageMin}
            max={ageMax}
            step="1"
            value={parseFloatOrDefault(formData.age, 30)}
            onChange={handleAgeChange}
            aria-label="Возраст"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
        </div>
        
        {/* Вес */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="weightSlider" className="text-gray-600 dark:text-gray-400">Вес</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.weight} {formData.measurementSystem === 'metric' ? 'кг' : 'фунт'}
            </span>
          </div>
          <input
            type="range"
            id="weightSlider"
            min={weightMin}
            max={weightMax}
            step={formData.measurementSystem === 'metric' ? 1 : 2}
            value={parseFloatOrDefault(formData.weight, formData.gender === 'male' ? 75 : 60)}
            onChange={handleWeightChange}
            aria-label="Вес"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
        </div>
        
        {/* Рост */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="heightSlider" className="text-gray-600 dark:text-gray-400">Рост</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.height} {formData.measurementSystem === 'metric' ? 'см' : 'дюйм'}
            </span>
          </div>
          <input
            type="range"
            id="heightSlider"
            min={heightMin}
            max={heightMax}
            step={formData.measurementSystem === 'metric' ? 1 : 1}
            value={parseFloatOrDefault(formData.height, formData.gender === 'male' ? 175 : 165)}
            onChange={handleHeightChange}
            aria-label="Рост"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
        </div>
        
        {/* Готовые профили */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Готовые профили</div>
          <div className="grid grid-cols-2 gap-2">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  age: suggestion.age, 
                  weight: formData.measurementSystem === 'metric' 
                    ? suggestion.weight 
                    : Math.round(parseFloat(suggestion.weight) * 2.20462),
                  height: formData.measurementSystem === 'metric' 
                    ? suggestion.height 
                    : Math.round(parseFloat(suggestion.height) * 0.393701)
                }))}
                className="p-2 text-xs rounded-lg border transition-colors bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                {suggestion.age} лет, {suggestion.weight} {formData.measurementSystem === 'metric' ? 'кг' : 'фунт'}
              </button>
            ))}
          </div>
        </div>
        
        {/* Активность */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Уровень активности</div>
          <div className="grid grid-cols-1 gap-2">
            {activityLevels.slice(0, 3).map(level => (
              <button
                key={level.id}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, activityLevel: level.id }))}
                className={`p-2 text-sm rounded-lg border transition-colors flex flex-col
                  ${formData.activityLevel === level.id 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <span className="font-medium">{level.name}</span>
                <span className="text-xs mt-1 opacity-80">{level.description.split(',')[0]}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Цели */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Цель</div>
          <div className="grid grid-cols-1 gap-2">
            {goals.filter(g => ['lose', 'maintain', 'gain'].includes(g.id)).map(goal => (
              <button
                key={goal.id}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, goal: goal.id }))}
                className={`p-2 text-sm rounded-lg border transition-colors flex flex-col
                  ${formData.goal === goal.id 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <span className="font-medium">{goal.name}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка рассчета */}
        <Button
          primary
          fullWidth
          onClick={calculateTDEE}
          icon={<Activity className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать TDEE
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;